// product-selection-store.ts
import { create, StoreApi, UseBoundStore } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Product, BumpProduct } from "../shared/types";

// Type definitions
export interface ProductSelectionState {
  // State
  products: Product[];
  bumpProducts: BumpProduct[];
  selectedProductIndex: number | null;
  selectedBumps: number[];
  isInitialized: boolean;
  error: string | null;

  // Derived data
  selectedProduct: Product | null;
  skuPriceMap: Record<string, number>;
  skuDetailsMap: Record<string, { price: number; name: string }>;
  finalPrice: number;

  // Actions
  initialize: (products: Product[], bumpProducts: BumpProduct[]) => void;
  selectProduct: (index: number) => void;
  toggleBump: (index: number, addBump: boolean) => void;
  reset: () => void;
}

// Store instances cache
const storeInstances: Record<string, UseBoundStore<StoreApi<ProductSelectionState>>> = {};

/**
 * Factory function to create or retrieve store instances by ID
 * @param id - Unique identifier for the component instance
 * @returns A Zustand store instance specific to the provided ID
 */
export const createProductSelectionStore = (id: string): UseBoundStore<StoreApi<ProductSelectionState>> => {
  // Return cached instance if exists
  if (storeInstances[id]) {
    return storeInstances[id];
  }

  // Create new store instance for this ID
  const storageKey = `product-selection-storage-${id}`;
  
  const store = create<ProductSelectionState>()(
    persist(
      (set, get) => ({
        // Initial state
        products: [],
        bumpProducts: [],
        selectedProductIndex: 0,
        selectedBumps: [],
        selectedProduct: null,
        skuPriceMap: {},
        skuDetailsMap: {},
        finalPrice: 0,
        isInitialized: false,
        error: null,

        // Initialize the store with products data
        initialize: (products, bumpProducts) => {
          try {
            if (!products || !products.length) {
              return set({ 
                error: 'No products provided for initialization',
                isInitialized: false 
              });
            }

            set({
              products,
              bumpProducts: bumpProducts || [],
              selectedProductIndex: 0,
              selectedBumps: [],
              error: null,
            });
            
            // Update derived state after setting initial values
            const selectedProduct = products[0];
            
            if (!selectedProduct) {
              return set({ 
                error: 'Failed to select initial product',
                isInitialized: false 
              });
            }
            
            // Generate SKU-to-Price map
            const skuPriceMap = {
              [selectedProduct.sku]: selectedProduct.price * 100,
            };
            
            // Generate SKU-to-Details map
            const skuDetailsMap = {
              [selectedProduct.sku]: {
                price: selectedProduct.price * 100,
                name: selectedProduct.name,
              },
            };
            
            set({
              selectedProduct,
              skuPriceMap,
              skuDetailsMap,
              finalPrice: selectedProduct.price * 100,
              isInitialized: true,
            });
          } catch (err) {
            console.error('Error initializing product selection store:', err);
            set({ 
              error: err instanceof Error ? err.message : 'Unknown error during initialization',
              isInitialized: false 
            });
          }
        },

        // Select a product
        selectProduct: (index) => {
          set((state) => {
            try {
              if (index < 0 || index >= state.products.length) {
                return { 
                  error: `Invalid product index: ${index}`,
                };
              }

              const newSelectedProduct = state.products[index];
              let newSelectedBumps = [...state.selectedBumps];
              
              // Clear bumps if product hides order bumps
              if (newSelectedProduct.hideOrderBump) {
                newSelectedBumps = [];
              }
              
              // Generate new SKU-to-Price map
              const newSkuPriceMap = {
                [newSelectedProduct.sku]: newSelectedProduct.price * 100,
                ...newSelectedBumps.reduce((acc, bumpIndex) => {
                  const bumpProduct = state.bumpProducts[bumpIndex];
                  if (bumpProduct) {
                    acc[bumpProduct.sku] = bumpProduct.price * 100;
                  }
                  return acc;
                }, {} as Record<string, number>),
              };
              
              // Generate new SKU-to-Details map
              const newSkuDetailsMap = {
                [newSelectedProduct.sku]: {
                  price: newSelectedProduct.price * 100,
                  name: newSelectedProduct.name,
                },
                ...newSelectedBumps.reduce((acc, bumpIndex) => {
                  const bumpProduct = state.bumpProducts[bumpIndex];
                  if (bumpProduct) {
                    acc[bumpProduct.sku] = {
                      price: bumpProduct.price * 100,
                      name: bumpProduct.name,
                    };
                  }
                  return acc;
                }, {} as Record<string, { price: number; name: string }>),
              };
              
              // Calculate final price
              let finalPrice = newSkuPriceMap[newSelectedProduct.sku] || 0;
              newSelectedBumps.forEach((bumpIndex) => {
                const bumpProduct = state.bumpProducts[bumpIndex];
                if (bumpProduct) {
                  finalPrice += newSkuPriceMap[bumpProduct.sku] || 0;
                }
              });
              
              return {
                selectedProductIndex: index,
                selectedProduct: newSelectedProduct,
                selectedBumps: newSelectedBumps,
                skuPriceMap: newSkuPriceMap,
                skuDetailsMap: newSkuDetailsMap,
                finalPrice,
                error: null,
              };
            } catch (err) {
              console.error('Error selecting product:', err);
              return { 
                error: err instanceof Error ? err.message : 'Unknown error selecting product',
              };
            }
          });
        },

        // Toggle a bump product
        toggleBump: (index, addBump) => {
          set((state) => {
            try {
              if (index < 0 || index >= (state.bumpProducts?.length || 0)) {
                return { 
                  error: `Invalid bump product index: ${index}`,
                };
              }
              
              if (!state.selectedProduct) {
                return {
                  error: 'No product selected',
                };
              }

              const updatedBumps = addBump
                ? [...state.selectedBumps, index]
                : state.selectedBumps.filter((i) => i !== index);
              
              // Generate new SKU-to-Price map
              const newSkuPriceMap = {
                [state.selectedProduct.sku]: state.selectedProduct.price * 100,
                ...updatedBumps.reduce((acc, bumpIndex) => {
                  const bumpProduct = state.bumpProducts[bumpIndex];
                  if (bumpProduct) {
                    acc[bumpProduct.sku] = bumpProduct.price * 100;
                  }
                  return acc;
                }, {} as Record<string, number>),
              };
              
              // Generate new SKU-to-Details map
              const newSkuDetailsMap = {
                [state.selectedProduct.sku]: {
                  price: state.selectedProduct.price * 100,
                  name: state.selectedProduct.name,
                },
                ...updatedBumps.reduce((acc, bumpIndex) => {
                  const bumpProduct = state.bumpProducts[bumpIndex];
                  if (bumpProduct) {
                    acc[bumpProduct.sku] = {
                      price: bumpProduct.price * 100,
                      name: bumpProduct.name,
                    };
                  }
                  return acc;
                }, {} as Record<string, { price: number; name: string }>),
              };
              
              // Calculate final price
              let finalPrice = newSkuPriceMap[state.selectedProduct.sku] || 0;
              updatedBumps.forEach((bumpIndex) => {
                const bumpProduct = state.bumpProducts[bumpIndex];
                if (bumpProduct) {
                  finalPrice += newSkuPriceMap[bumpProduct.sku] || 0;
                }
              });
              
              return {
                selectedBumps: updatedBumps,
                skuPriceMap: newSkuPriceMap,
                skuDetailsMap: newSkuDetailsMap,
                finalPrice,
                error: null,
              };
            } catch (err) {
              console.error('Error toggling bump product:', err);
              return { 
                error: err instanceof Error ? err.message : 'Unknown error toggling bump product',
              };
            }
          });
        },

        // Reset store to initial state
        reset: () => {
          set({
            products: [],
            bumpProducts: [],
            selectedProductIndex: 0,
            selectedBumps: [],
            selectedProduct: null,
            skuPriceMap: {},
            skuDetailsMap: {},
            finalPrice: 0,
            isInitialized: false,
            error: null,
          });
        }
      }),
      {
        name: storageKey,
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          selectedProductIndex: state.selectedProductIndex,
          selectedBumps: state.selectedBumps,
        }),
      }
    )
  );

  // Cache the store instance
  storeInstances[id] = store;
  
  return store;
};

// Legacy singleton store for backward compatibility
export const useProductSelectionStore = createProductSelectionStore('default');
